import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Calculator, TrendingUp } from 'lucide-react';

export function ROICalculatorModal() {
  const [caseVolume, setCaseVolume] = useState(80);
  const [avgReimbursement, setAvgReimbursement] = useState(8500);
  const [leakagePercent, setLeakagePercent] = useState(15);

  const results = useMemo(() => {
    const annualRevenue = caseVolume * 12 * avgReimbursement;
    const annualLeakage = annualRevenue * (leakagePercent / 100);
    const recoveryRate = 0.82; // LINK recovery rate
    const projected = Math.round(annualLeakage * recoveryRate);
    return { annualRevenue, annualLeakage, projected };
  }, [caseVolume, avgReimbursement, leakagePercent]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 text-xs">
          <Calculator className="w-3.5 h-3.5" />
          ROI Calculator
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-success" />
            Procedure Revenue Integrity ROI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Monthly Case Volume */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Monthly Case Volume</label>
              <span className="font-mono text-sm font-semibold text-foreground">{caseVolume}</span>
            </div>
            <Slider
              value={[caseVolume]}
              onValueChange={([v]) => setCaseVolume(v)}
              min={10}
              max={500}
              step={5}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          {/* Avg Reimbursement */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Avg. Reimbursement per Case</label>
              <span className="font-mono text-sm font-semibold text-foreground">
                ${avgReimbursement.toLocaleString()}
              </span>
            </div>
            <Slider
              value={[avgReimbursement]}
              onValueChange={([v]) => setAvgReimbursement(v)}
              min={2000}
              max={25000}
              step={500}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          {/* Leakage % */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Estimated Leakage %</label>
              <span className="font-mono text-sm font-semibold text-warning">{leakagePercent}%</span>
            </div>
            <Slider
              value={[leakagePercent]}
              onValueChange={([v]) => setLeakagePercent(v)}
              min={5}
              max={30}
              step={1}
              className="[&_[role=slider]]:bg-warning"
            />
          </div>

          {/* Results */}
          <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Projected Annual Recovery
            </p>
            <p className="text-3xl font-bold font-mono text-success tracking-tight">
              ${results.projected.toLocaleString()}
            </p>
            <div className="flex justify-center gap-6 text-[11px] text-muted-foreground">
              <span>Earned: ${results.annualRevenue.toLocaleString()}</span>
              <span className="text-warning">Leakage: ${results.annualLeakage.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
